/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { CarouselTemplate } from "../../model/enums";
import type { TCarouselItem } from "../../model/types";

/**
 * Screens that can open a See More page.
 *
 * `loadMoreSeeMoreItems()` (`seeMoreThunks.ts`) switches on this to decide
 * which backend to page — a My Hub carousel ID means nothing to the
 * smart-carousel endpoint and vice versa — so a caller must be one of these
 * rather than an arbitrary string. Deliberately its own enum rather than
 * `ScreenControllerTypes`: the same screen can own several carousels, and See
 * More only needs to tell their pages apart, not navigate to them.
 */
export enum SeeMoreScreen {
  HOME_SCREEN = "homeScreen",
  MY_HUB = "myHub",
  SEARCH_SCREEN = "searchScreen",
  SHOWS_AND_MOVIES = "showsAndMovies"
}

/** How a source screen expects a selected grid item to be handled. */
export type TSeeMoreSelectionMode =
  | "default"
  | "searchContent"
  | "searchPlayback";

/**
 * Where a See More page was opened from.
 *
 * Carried so that whoever fulfils pagination — and whoever reads the logs —
 * can tell one caller's grid from another's, and route it to the right
 * backend (see {@link SeeMoreScreen}).
 */
export interface TSeeMoreSource {
  /** Screen that opened the page — routes pagination to that screen's backend. */
  screen: SeeMoreScreen;
  /** Caller's identifier for the carousel within that screen, when it has one. */
  carouselId?: string;
  /**
   * Index of the source carousel on the screen that opened the page.
   *
   * Carried only so a selection made here reports the same `list_position` the
   * rail it came from would have reported — the grid itself is one flat list
   * and has no rows of its own.
   */
  carouselIndex?: number;
  /** Showflix API genre IDs captured when a Shows & Movies grid is opened. */
  showflixGenres?: string[];
  /** Normalized Search query required for Search carousel pagination. */
  searchTerm?: string;
  /** Serializable item selection behaviour requested by the source screen. */
  selectionMode?: TSeeMoreSelectionMode;
}

/**
 * Everything a screen must hand over to open a See More page.
 *
 * This is the whole contract between a caller and the See More screen — the
 * screen reads nothing else about where it came from, which is what lets My
 * Hub, Home and any later screen share it unchanged.
 */
export interface TSeeMoreRequest {
  /** Page heading — normally the source carousel's own title. */
  title: string;
  /**
   * Template the source carousel was rendering. The grid reuses it verbatim,
   * so the tiles on the See More page are the same shape and size as the ones
   * on the rail the user came from.
   */
  template: CarouselTemplate;
  /** Items the caller already holds. Rendered immediately, before any paging. */
  items: TCarouselItem[];
  /**
   * True length of the collection when the caller knows it (My Hub's
   * `resolvedCount`). Drives the "there is more to fetch" test; omit when the
   * handed-over items are already the whole set.
   */
  totalCount?: number;
  /** Cursor for the next page when the source uses cursor pagination. */
  pageCursor?: string | null;
  /** Opaque origin descriptor — see {@link TSeeMoreSource}. */
  source?: TSeeMoreSource;
  /**
   * Column override. Omit to take the count the template implies, which is
   * what the Figma pages are drawn at.
   */
  columns?: number;
  /**
   * Caption flavour under each tile. Defaults to `"content"` — the catalogue
   * metadata the Figma page shows.
   */
  previewVariant?: TSeeMorePreviewVariant;
  /**
   * Where BACK goes when this page is the first entry in browser history —
   * normally the URL of the screen that opened it.
   */
  returnUrl: string;
}

/**
 * Which flavour of tile metadata the page renders under each tile.
 *
 * The tile *shape* comes from the template, but the caption under it depends
 * on what the items are, and only the caller knows that: My Hub's grids are
 * recordings ("Recorded on…"), a Home rail's are catalogue entries ("Movie ‧
 * 2016 ‧ TV-14"). A render callback would express this better but cannot
 * survive a Redux round-trip, so the choice travels as a serializable tag.
 */
export type TSeeMorePreviewVariant = "content" | "recording";

/** A fetched cursor-pagination page to append to the current grid. */
export interface TSeeMoreCursorPage {
  /** Items returned by the requested page. */
  items: TCarouselItem[];
  /** Cursor returned for the following page, or null when exhausted. */
  pageCursor: string | null;
}

/** Redux state for the See More grid page. */
export interface SeeMoreState {
  /** True once a caller has opened the page; false after it is closed. */
  isOpen: boolean;
  /** Page heading. */
  title: string;
  /** Template every tile on the page is rendered with. */
  template: CarouselTemplate;
  /** Items currently laid out. */
  items: TCarouselItem[];
  /** True collection length, or 0 when the caller did not supply one. */
  totalCount: number;
  /** Current cursor for sources that paginate with an opaque cursor. */
  pageCursor: string | null;
  /** Opaque origin descriptor, or null when the caller did not supply one. */
  source: TSeeMoreSource | null;
  /** Column override from the request, or 0 for "use the template's default". */
  requestedColumns: number;
  /** Caption flavour under each tile. */
  previewVariant: TSeeMorePreviewVariant;
  /** BACK fallback URL. */
  returnUrl: string;
  /** Flat index of the focused cell. */
  focusedIndex: number;
  /**
   * Column count the grid actually resolved to.
   *
   * Reported by the view rather than derived here: the reducers need it for
   * D-pad arithmetic, and the alternative — recomputing the layout inside the
   * slice — would put the same geometry in two places and let them disagree.
   */
  columns: number;
  /** True while a page of additional items is in flight. */
  isLoadingMore: boolean;
}
